import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  Paper,
  Stack,
  Chip,
  Button,
  Divider
} from '@mui/material';
import {
  ChecklistRtl,
  ExpandMore,
  Print,
  Download,
  CalendarMonth,
  Home,
  LocalShipping,
  Description,
  AccountBalance,
  Phone
} from '@mui/icons-material';

function MovingChecklist() {
  const [checkedItems, setCheckedItems] = useState({});

  const handleCheckChange = (category, index) => {
    const key = `${category}-${index}`;
    setCheckedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const checklists = [
    {
      title: '3 Monate vor dem Umzug',
      icon: <CalendarMonth sx={{ color: '#55d6dc' }} />,
      priority: 'Sehr wichtig',
      items: [
        'Alten Mietvertrag kündigen (Kündigungsfrist beachten)',
        'Neuen Mietvertrag unterschreiben',
        'Umzugsunternehmen recherchieren und Angebote einholen',
        'Umzugsurlaub beim Arbeitgeber beantragen',
        'Budget für den Umzug festlegen',
        'Entrümpelung beginnen - was wird nicht mitgenommen?',
        'Kindergarten/Schule über Umzug informieren'
      ]
    },
    {
      title: '2 Monate vor dem Umzug',
      icon: <Home sx={{ color: '#55d6dc' }} />,
      priority: 'Wichtig',
      items: [
        'Umzugsunternehmen beauftragen',
        'Umzugskartons und Verpackungsmaterial besorgen',
        'Nachmieter suchen (falls erforderlich)',
        'Renovierungsarbeiten planen',
        'Versicherungen über Umzug informieren',
        'Strom-, Gas- und Wasseranbieter kündigen/ummelden',
        'Internet und Telefon ummelden',
        'GEZ/Rundfunkbeitrag ummelden'
      ]
    },
    {
      title: '1 Monat vor dem Umzug',
      icon: <LocalShipping sx={{ color: '#55d6dc' }} />,
      priority: 'Wichtig',
      items: [
        'Nachsendeauftrag bei der Post einrichten',
        'Halteverbotszone für Umzugstag beantragen',
        'Mit dem Packen beginnen (selten genutzte Gegenstände)',
        'Möbel ausmessen und Raumplan für neue Wohnung erstellen',
        'Adressänderungen bei Banken und Versicherungen',
        'Verträge und Abonnements ummelden',
        'Arzt und Zahnarzt informieren, ggf. Unterlagen anfordern',
        'Haustiere beim neuen Amt anmelden'
      ]
    },
    {
      title: '2 Wochen vor dem Umzug',
      icon: <Description sx={{ color: '#55d6dc' }} />,
      priority: 'Dringend',
      items: [
        'Systematisch packen und Kartons beschriften',
        'Wichtige Dokumente in separatem Ordner sammeln',
        'Schlüsselübergabe mit Vermieter vereinbaren',
        'Umzugshelfer organisieren und informieren',
        'Verpflegung für Umzugstag planen',
        'Putzplan für alte Wohnung erstellen',
        'Wertsachen und wichtige Medikamente separat packen',
        'Kühlschrank und Gefrierschrank abtauen'
      ]
    },
    {
      title: '1 Woche vor dem Umzug',
      icon: <AccountBalance sx={{ color: '#55d6dc' }} />,
      priority: 'Sehr dringend',
      items: [
        'Bargeld für Umzugstag abheben (Trinkgeld, Verpflegung)',
        'Erste-Hilfe-Karton packen (für erste Nacht)',
        'Reinigungsmittel bereitstellen',
        'Werkzeugkiste griffbereit halten',
        'Letzte Sachen packen',
        'Pflanzen für Transport vorbereiten',
        'Nachbarn über Umzugstag informieren'
      ]
    },
    {
      title: 'Am Umzugstag',
      icon: <Phone sx={{ color: '#55d6dc' }} />,
      priority: 'Kritisch',
      items: [
        'Zählerstände ablesen und dokumentieren',
        'Wohnungsübergabe alte Wohnung durchführen',
        'Übergabeprotokoll unterschreiben',
        'Umzugsgut auf Vollständigkeit prüfen',
        'Transportschäden sofort dokumentieren',
        'Neue Wohnung auf Mängel prüfen',
        'Neue Schlüssel erhalten',
        'Namensschild anbringen'
      ]
    },
    {
      title: 'Nach dem Umzug',
      icon: <ChecklistRtl sx={{ color: '#55d6dc' }} />,
      priority: 'Nachbereitung',
      items: [
        'Einwohnermeldeamt: Ummeldung (innerhalb von 2 Wochen)',
        'KFZ ummelden',
        'Neue Adresse allen wichtigen Stellen mitteilen',
        'Daueraufträge anpassen',
        'Neue Hausarztpraxis suchen',
        'Handwerker für Restarbeiten beauftragen',
        'Kaution der alten Wohnung zurückfordern'
      ]
    }
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Kritisch': return '#ff4444';
      case 'Sehr dringend': return '#ff8800';
      case 'Dringend': return '#ffbb00';
      case 'Sehr wichtig': return '#55d6dc';
      case 'Wichtig': return '#66bb6a';
      default: return '#888888';
    }
  };

  const calculateProgress = () => {
    const totalItems = checklists.reduce((sum, cat) => sum + cat.items.length, 0);
    const checkedCount = Object.values(checkedItems).filter(Boolean).length;
    return Math.round((checkedCount / totalItems) * 100);
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a1f28 0%, #162a34 50%, #1c3440 100%)'
    }}>
      <Container maxWidth="lg" sx={{ pt: 12, pb: 6 }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <ChecklistRtl sx={{ fontSize: 80, color: '#55d6dc', mb: 3 }} />
          <Typography variant="h2" sx={{ color: 'white', fontWeight: 'bold', mb: 3 }}>
            Umzugscheckliste
          </Typography>
          <Typography variant="h5" sx={{ color: '#55d6dc', mb: 2 }}>
            Ihre komplette Checkliste für einen organisierten Umzug
          </Typography>
          <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, maxWidth: 800, mx: 'auto' }}>
            Mit unserer detaillierten Checkliste vergessen Sie garantiert nichts. 
            Von der ersten Planung bis zur Ummeldung - alles Wichtige auf einen Blick.
          </Typography>
        </Box>

        {/* Progress Section */}
        <Paper sx={{ 
          p: 4, 
          mb: 6, 
          bgcolor: 'rgba(85, 214, 220, 0.1)', 
          border: '1px solid rgba(85, 214, 220, 0.3)',
          borderRadius: 2
        }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box>
              <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
                Ihr Fortschritt
              </Typography>
              <Typography variant="h3" sx={{ color: '#55d6dc', fontWeight: 'bold' }}>
                {calculateProgress()}% erledigt
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <Button
                startIcon={<Print />}
                variant="outlined"
                sx={{ 
                  color: '#55d6dc', 
                  borderColor: '#55d6dc',
                  '&:hover': {
                    borderColor: '#4bc2c8',
                    backgroundColor: 'rgba(85, 214, 220, 0.1)'
                  }
                }}
                onClick={() => window.print()}
              >
                Drucken
              </Button>
              <Button
                startIcon={<Download />}
                variant="contained"
                sx={{ 
                  bgcolor: '#55d6dc', 
                  color: '#0a1f28',
                  '&:hover': {
                    bgcolor: '#4bc2c8'
                  }
                }}
              >
                Als PDF
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* Checklists */}
        <Box sx={{ mb: 8 }}>
          {checklists.map((checklist, categoryIndex) => (
            <Accordion
              key={categoryIndex}
              sx={{
                mb: 2,
                bgcolor: '#1a3a44',
                color: 'white',
                '&:before': { display: 'none' }
              }}
              defaultExpanded={categoryIndex === 0}
            >
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ color: '#55d6dc' }} />}
                sx={{
                  '&:hover': { bgcolor: '#275664' }
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                  {checklist.icon}
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {checklist.title}
                    </Typography>
                  </Box>
                  <Chip 
                    label={checklist.priority}
                    size="small"
                    sx={{ 
                      bgcolor: getPriorityColor(checklist.priority),
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {checklist.items.map((item, itemIndex) => (
                    <ListItem key={itemIndex} sx={{ py: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={checkedItems[`${categoryIndex}-${itemIndex}`] || false}
                            onChange={() => handleCheckChange(categoryIndex, itemIndex)}
                            sx={{
                              color: '#55d6dc',
                              '&.Mui-checked': {
                                color: '#55d6dc'
                              }
                            }}
                          />
                        }
                        label={
                          <Typography 
                            sx={{ 
                              color: 'white',
                              textDecoration: checkedItems[`${categoryIndex}-${itemIndex}`] ? 'line-through' : 'none',
                              opacity: checkedItems[`${categoryIndex}-${itemIndex}`] ? 0.6 : 1
                            }}
                          >
                            {item}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Tips Section */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{
              bgcolor: '#275664',
              borderRadius: 2,
              border: '1px solid rgba(255,255,255,0.08)',
              height: '100%'
            }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: '#ffd700', fontWeight: 'bold', mb: 2 }}>
                  💡 Profi-Tipp
                </Typography>
                <Typography sx={{ color: 'white', opacity: 0.9 }}>
                  Beginnen Sie frühzeitig mit der Planung. 3 Monate Vorlaufzeit sind ideal für einen stressfreien Umzug.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{
              bgcolor: '#275664',
              borderRadius: 2,
              border: '1px solid rgba(255,255,255,0.08)',
              height: '100%'
            }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: '#ffd700', fontWeight: 'bold', mb: 2 }}>
                  ⚠️ Wichtig
                </Typography>
                <Typography sx={{ color: 'white', opacity: 0.9 }}>
                  Die Ummeldung beim Einwohnermeldeamt muss innerhalb von 2 Wochen nach dem Umzug erfolgen.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{
              bgcolor: '#275664',
              borderRadius: 2,
              border: '1px solid rgba(255,255,255,0.08)',
              height: '100%'
            }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: '#ffd700', fontWeight: 'bold', mb: 2 }}>
                  💰 Spartipp
                </Typography>
                <Typography sx={{ color: 'white', opacity: 0.9 }}>
                  Umzugskosten können steuerlich abgesetzt werden. Bewahren Sie alle Belege auf!
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* CTA Section */}
        <Box sx={{ 
          bgcolor: 'rgba(255,215,0,0.1)', 
          border: '2px solid #ffd700',
          borderRadius: 3,
          p: 4,
          textAlign: 'center'
        }}>
          <Typography variant="h4" sx={{ color: 'white', mb: 2, fontWeight: 'bold' }}>
            Benötigen Sie Unterstützung?
          </Typography>
          <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, mb: 3 }}>
            Wir helfen Ihnen gerne bei der Planung und Durchführung Ihres Umzugs
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Box
              component="button"
              onClick={() => window.location.href = '/moving-costs'}
              style={{
                backgroundColor: '#55d6dc',
                color: '#275664',
                padding: '12px 32px',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer'
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#4bc2c8')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#55d6dc')}
            >
              Kosten berechnen
            </Box>
            <Box
              component="button"
              onClick={() => window.location.href = '/contact'}
              style={{
                backgroundColor: 'transparent',
                color: '#ffd700',
                padding: '12px 32px',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                border: '2px solid #ffd700',
                borderRadius: 4,
                cursor: 'pointer'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,215,0,0.1)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              Beratung anfordern
            </Box>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}

export default MovingChecklist;